import { useState } from "react";
import {
  Shield,
  Save,
  Plus,
  Edit,
  Archive,
  Info,
  ChevronDown,
  X,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Badge,
  Tabs,
  useToast,
  formatDate,
} from "../adminComponents/adminComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminSettingsHook } from "../../hooks/useAdminHooks";
import "./settings.css";

const COHORT_BADGE_TONES = {
  open: "success",
  closed: "muted",
  archived: "default",
};

const LEVEL_COLORS = {
  1: "hsl(220,14%,50%)",
  2: "hsl(64,53%,46%)",
  3: "hsl(181,57%,42%)",
  4: "hsl(327,72%,42%)",
  5: "hsl(8,75%,58%)",
};

const DEFAULT_SETTINGS_DATA = {
  organization: {
    profile: {
      name: "Imagine Her",
      email: "info@imagineher.org",
      phone: "+256 312 345 678",
      address: "Plot 45, Kampala Road, Kampala, Uganda",
      website: "https://imagineher.org",
      description:
        "Empowering women entrepreneurs across East Africa through mentorship, capital, and community.",
    },
  },
  cohorts: [
    {
      id: "1",
      name: "2024-Q2",
      start_date: "2024-04-01",
      end_date: "2024-06-30",
      status: "open",
      venture_count: 8,
    },
  ],
  data_collection: {
    settings: {
      reporting_period: "monthly",
      submission_deadline: 10,
      reminder_frequency: 7,
      max_revenue_change: 400,
      stale_data_period_days: 60,
      auto_lock_period: true,
    },
  },
  graduation: {
    levels: [
      {
        level: 1,
        name: "Seedling",
        criteria: "Enrolled in program, idea stage",
        revenue_min: 0,
        revenue_max: 0,
        employees: "0",
      },
      {
        level: 2,
        name: "Sprout",
        criteria: "First revenue generated",
        revenue_min: 1,
        revenue_max: 1000000,
        employees: "1-2",
      },
      {
        level: 3,
        name: "Sapling",
        criteria: "Consistent revenue, small team",
        revenue_min: 1000000,
        revenue_max: 5000000,
        employees: "3-5",
      },
      {
        level: 4,
        name: "Tree",
        criteria: "Growing business, established team",
        revenue_min: 5000000,
        revenue_max: 20000000,
        employees: "6-10",
      },
      {
        level: 5,
        name: "Redwood",
        criteria: "Scale-ready, external funding secured",
        revenue_min: 20000000,
        revenue_max: null,
        employees: "10+",
      },
    ],
  },
  roles: [
    {
      name: "admin",
      label: "Administrator",
      description:
        "Full system access with all permissions including user management.",
      permissions: [
        { key: "read", description: "View data and reports" },
        { key: "write", description: "Create and edit records" },
        { key: "delete", description: "Remove records permanently" },
        {
          key: "manage_users",
          description: "Add, edit, and deactivate users",
        },
        { key: "view_all", description: "Access all ventures and data" },
        { key: "export", description: "Export data to files" },
      ],
    },
    {
      name: "program_manager",
      label: "Program Manager",
      description: "Manages portfolio ventures and can view all data.",
      permissions: [
        { key: "read", description: "View data and reports" },
        { key: "write", description: "Create and edit records" },
        { key: "view_all", description: "Access all ventures and data" },
        { key: "export", description: "Export data to files" },
      ],
    },
    {
      name: "funder",
      label: "Funder / Partner",
      description: "External partner with read-only access to aggregate data.",
      permissions: [
        { key: "read", description: "View data and reports" },
        {
          key: "view_aggregate",
          description: "View aggregated portfolio data",
        },
      ],
    },
    {
      name: "venture",
      label: "Venture / Entrepreneur",
      description: "Entrepreneur with access to their own venture data only.",
      permissions: [
        { key: "read_own", description: "View own venture data only" },
        { key: "write_own", description: "Edit own venture data only" },
      ],
    },
  ],
  system: {
    platform_version: "1.0.0",
    last_data_sync: "2026-05-19T12:00:00Z",
    database_status: { value: "Connected" },
    support: {
      email: "support@imagineher.org",
      phone: "+256 312 345 678",
      business_hours: "9AM - 5PM EAT",
      help_message:
        "Contact our support team at support@imagineher.org or call +256 312 345 678 during business hours (9AM - 5PM EAT).",
    },
  },
};

function fmtUGX(value) {
  if (value === null) return "INF";
  if (value === 0) return "UGX 0";
  if (value >= 1000000) return `UGX ${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `UGX ${(value / 1000).toFixed(0)}K`;
  return `UGX ${value}`;
}

function mapDataSettings(settings) {
  return {
    reportingPeriod: settings?.reporting_period ?? "monthly",
    submissionDeadline: Number(settings?.submission_deadline ?? 10),
    reminderFrequency: Number(settings?.reminder_frequency ?? 7),
    maxRevenueChangePercent: Number(settings?.max_revenue_change ?? 400),
    staleDataThreshold: Number(settings?.stale_data_period_days ?? 60),
    autoLockPeriod: Boolean(settings?.auto_lock_period),
  };
}

function mapCohorts(cohorts) {
  return (cohorts ?? []).map((cohort) => ({
    id: cohort.id,
    name: cohort.name,
    startDate: cohort.start_date,
    endDate: cohort.end_date,
    status: cohort.status,
    ventureCount: Number(cohort.venture_count ?? 0),
  }));
}

export default function Settings() {
  const { user, token } = useAuth();
  const { toast, ToastViewport } = useToast();
  const [activeTab, setActiveTab] = useState("organization");
  const [orgOverrides, setOrgOverrides] = useState(null);
  const [dataOverrides, setDataOverrides] = useState(null);
  const [cohortOverrides, setCohortOverrides] = useState(null);
  const [addCohortOpen, setAddCohortOpen] = useState(false);
  const [newCohort, setNewCohort] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const [openAccordion, setOpenAccordion] = useState(null);

  const shouldFetchApi = user?.role === ROLES.ADMIN;

  const { data: settingsApiData, isLoading } = useAdminSettingsHook({
    token,
    userId: user?.id,
    enabled: shouldFetchApi,
  });

  const settingsData = settingsApiData ?? DEFAULT_SETTINGS_DATA;
  const orgProfile =
    orgOverrides ?? settingsData?.organization?.profile ?? DEFAULT_SETTINGS_DATA.organization.profile;
  const dataSettings =
    dataOverrides ??
    mapDataSettings(settingsData?.data_collection?.settings);
  const cohorts =
    cohortOverrides ??
    mapCohorts(settingsData?.cohorts ?? DEFAULT_SETTINGS_DATA.cohorts);
  const graduationLevels = settingsData?.graduation?.levels ?? [];
  const roles = settingsData?.roles ?? [];
  const system = settingsData?.system ?? DEFAULT_SETTINGS_DATA.system;

  const TABS = [
    { value: "organization", label: "Organization" },
    { value: "cohorts", label: "Cohorts" },
    { value: "data", label: "Data Collection" },
    { value: "graduation", label: "Graduation" },
    { value: "roles", label: "Roles" },
    { value: "system", label: "System" },
  ];

  function setOrg_(key) {
    return (event) => {
      const value = event.target.value;
      setOrgOverrides((current) => ({
        ...(current ?? orgProfile),
        [key]: value,
      }));
    };
  }

  function setData_(key) {
    return (event) => {
      const raw =
        event.target.type === "number"
          ? Number(event.target.value)
          : event.target.value;
      setDataOverrides((current) => ({
        ...(current ?? dataSettings),
        [key]: raw,
      }));
    };
  }

  function handleSaveOrg() {
    toast({
      title: "Settings Saved",
      description: "Organization settings have been updated.",
    });
  }

  function handleSaveData() {
    toast({
      title: "Settings Saved",
      description: "Data collection settings have been updated.",
    });
  }

  function handleAddCohort() {
    const created = {
      id: String(Date.now()),
      name: newCohort.name,
      startDate: newCohort.startDate,
      endDate: newCohort.endDate,
      status: "open",
      ventureCount: 0,
    };

    setCohortOverrides([created, ...cohorts]);
    toast({
      title: "Cohort Created",
      description: `${newCohort.name} has been added.`,
    });
    setAddCohortOpen(false);
    setNewCohort({ name: "", startDate: "", endDate: "" });
  }

  function handleArchiveCohort(cohort) {
    setCohortOverrides(
      cohorts.map((entry) =>
        entry.id === cohort.id ? { ...entry, status: "archived" } : entry,
      ),
    );
    toast({
      title: "Cohort Archived",
      description: `${cohort.name} has been archived.`,
    });
  }

  if (isLoading && shouldFetchApi) {
    return (
      <div className="adminSettings">
        <PageHeader
          title="Settings"
          description="Configure platform settings, cohorts, and data collection parameters."
        />
        <p className="admin-muted">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="adminSettings">
      <PageHeader
        title="Settings"
        description="Configure platform settings, cohorts, and data collection parameters."
      />

      <Tabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

      {activeTab === "organization" && (
        <Card
          title="Organization Profile"
          description="Manage your organization's public information and contact details."
        >
          <div className="admin-grid admin-grid-2">
            <div className="adminSettings-field">
              <label className="admin-label">Organization Name</label>
              <input
                className="admin-input adminSettings-full"
                value={orgProfile.name}
                onChange={setOrg_("name")}
              />
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">Contact Email</label>
              <input
                className="admin-input adminSettings-full"
                type="email"
                value={orgProfile.email}
                onChange={setOrg_("email")}
              />
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">Phone Number</label>
              <input
                className="admin-input adminSettings-full"
                value={orgProfile.phone}
                onChange={setOrg_("phone")}
              />
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">Website</label>
              <input
                className="admin-input adminSettings-full"
                value={orgProfile.website}
                onChange={setOrg_("website")}
              />
            </div>
          </div>
          <div className="adminSettings-field">
            <label className="admin-label">Address</label>
            <input
              className="admin-input adminSettings-full"
              value={orgProfile.address}
              onChange={setOrg_("address")}
            />
          </div>
          <div className="adminSettings-field">
            <label className="admin-label">Description</label>
            <textarea
              className="admin-textarea adminSettings-full"
              rows={3}
              value={orgProfile.description}
              onChange={setOrg_("description")}
            />
          </div>
          <div className="adminSettings-save-row">
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={handleSaveOrg}
            >
              <Save className="admin-btn-icon" />
              Save Changes
            </button>
          </div>
        </Card>
      )}

      {activeTab === "cohorts" && (
        <Card
          title="Cohort Management"
          description="Manage program cohorts and their timelines."
          actions={
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={() => setAddCohortOpen(true)}
            >
              <Plus className="admin-btn-icon" />
              Add Cohort
            </button>
          }
        >
          <div className="admin-grid admin-grid-2">
            {cohorts.map((cohort) => (
              <div key={cohort.id} className="adminSettings-cohort-card">
                <div className="adminSettings-cohort-header">
                  <span className="adminSettings-cohort-name">{cohort.name}</span>
                  <Badge tone={COHORT_BADGE_TONES[cohort.status] ?? "muted"}>
                    {cohort.status.charAt(0).toUpperCase() + cohort.status.slice(1)}
                  </Badge>
                </div>
                <div className="adminSettings-cohort-meta">
                  <div className="adminSettings-cohort-row">
                    <span className="admin-muted">Start Date</span>
                    <span>{formatDate(cohort.startDate)}</span>
                  </div>
                  <div className="adminSettings-cohort-row">
                    <span className="admin-muted">End Date</span>
                    <span>{formatDate(cohort.endDate)}</span>
                  </div>
                  <div className="adminSettings-cohort-row">
                    <span className="admin-muted">Ventures</span>
                    <span>{cohort.ventureCount}</span>
                  </div>
                </div>
                <div className="admin-row" style={{ marginTop: "0.75rem" }}>
                  <button
                    type="button"
                    className="admin-btn admin-btn-outline admin-btn-sm"
                    style={{ flex: 1 }}
                  >
                    <Edit className="admin-btn-icon" />
                    Edit
                  </button>
                  {cohort.status !== "archived" && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-outline admin-btn-sm"
                      style={{ flex: 1 }}
                      onClick={() => handleArchiveCohort(cohort)}
                    >
                      <Archive className="admin-btn-icon" />
                      Archive
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {activeTab === "data" && (
        <Card
          title="Data Collection Settings"
          description="Configure reporting periods, deadlines, and validation thresholds."
        >
          <div className="admin-grid admin-grid-2">
            <div className="adminSettings-field">
              <label className="admin-label">Reporting Period</label>
              <select
                className="admin-select adminSettings-full"
                value={dataSettings.reportingPeriod}
                onChange={setData_("reportingPeriod")}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">
                Submission Deadline (day of month)
              </label>
              <input
                className="admin-input adminSettings-full"
                type="number"
                min={1}
                max={28}
                value={dataSettings.submissionDeadline}
                onChange={setData_("submissionDeadline")}
              />
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">
                Reminder Frequency (days before due)
              </label>
              <input
                className="admin-input adminSettings-full"
                type="number"
                min={1}
                max={14}
                value={dataSettings.reminderFrequency}
                onChange={setData_("reminderFrequency")}
              />
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">
                Max Revenue Change % (flag outliers)
              </label>
              <input
                className="admin-input adminSettings-full"
                type="number"
                min={50}
                max={1000}
                value={dataSettings.maxRevenueChangePercent}
                onChange={setData_("maxRevenueChangePercent")}
              />
            </div>
            <div className="adminSettings-field">
              <label className="admin-label">Stale Data Threshold (days)</label>
              <input
                className="admin-input adminSettings-full"
                type="number"
                min={14}
                max={180}
                value={dataSettings.staleDataThreshold}
                onChange={setData_("staleDataThreshold")}
              />
            </div>
            <div className="adminSettings-toggle-row">
              <div>
                <p className="adminSettings-toggle-label">
                  Auto-lock Reporting Period
                </p>
                <p className="admin-muted adminSettings-toggle-desc">
                  Automatically lock periods after deadline
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={dataSettings.autoLockPeriod}
                className={
                  "adminSettings-toggle " +
                  (dataSettings.autoLockPeriod ? "adminSettings-toggle-on" : "")
                }
                onClick={() =>
                  setDataOverrides((current) => ({
                    ...(current ?? dataSettings),
                    autoLockPeriod: !dataSettings.autoLockPeriod,
                  }))
                }
              >
                <span className="adminSettings-toggle-thumb" />
              </button>
            </div>
          </div>
          <div className="adminSettings-save-row">
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={handleSaveData}
            >
              <Save className="admin-btn-icon" />
              Save Changes
            </button>
          </div>
        </Card>
      )}

      {activeTab === "graduation" && (
        <Card
          title="Graduation Level System"
          description="Define the criteria for each graduation level in the venture journey."
        >
          <div className="admin-stack">
            {graduationLevels.map((level) => (
              <div key={level.level} className="adminSettings-grad-row">
                <div
                  className="adminSettings-grad-circle"
                  style={{
                    background: LEVEL_COLORS[level.level] ?? "hsl(var(--primary))",
                  }}
                >
                  {level.level}
                </div>
                <div className="adminSettings-grad-info">
                  <div className="admin-row">
                    <span className="adminSettings-grad-name">{level.name}</span>
                    <Badge tone="muted">Level {level.level}</Badge>
                  </div>
                  <p
                    className="admin-muted"
                    style={{ fontSize: "0.8125rem", margin: 0 }}
                  >
                    {level.criteria}
                  </p>
                  <p
                    className="admin-muted"
                    style={{ fontSize: "0.75rem", margin: 0 }}
                  >
                    Revenue: {fmtUGX(level.revenue_min)} - {fmtUGX(level.revenue_max)}
                    &nbsp;|&nbsp; Employees: {level.employees}
                  </p>
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost admin-btn-sm"
                >
                  <Edit size={14} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {activeTab === "roles" && (
        <Card
          title="Roles & Permissions"
          description="Reference for user roles and their associated permissions."
        >
          <div className="admin-stack">
            {roles.map((role) => (
              <div key={role.name} className="adminSettings-accordion">
                <button
                  type="button"
                  className="adminSettings-accordion-trigger"
                  onClick={() =>
                    setOpenAccordion((current) =>
                      current === role.name ? null : role.name,
                    )
                  }
                  aria-expanded={openAccordion === role.name}
                >
                  <span className="admin-row">
                    <Shield size={14} />
                    {role.label}
                  </span>
                  <ChevronDown
                    size={16}
                    className={
                      "adminSettings-chevron" +
                      (openAccordion === role.name
                        ? " adminSettings-chevron-open"
                        : "")
                    }
                  />
                </button>
                {openAccordion === role.name && (
                  <div className="adminSettings-accordion-body">
                    <p
                      className="admin-muted"
                      style={{ fontSize: "0.8125rem", marginBottom: "0.75rem" }}
                    >
                      {role.description}
                    </p>
                    <div className="admin-stack" style={{ gap: "0.4rem" }}>
                      {(role.permissions ?? []).map((permission) => (
                        <div key={`${role.name}-${permission.key}`} className="admin-row">
                          <Badge tone="muted">{permission.key}</Badge>
                          <span
                            className="admin-muted"
                            style={{ fontSize: "0.8125rem" }}
                          >
                            {permission.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {activeTab === "system" && (
        <Card
          title="System Information"
          description="Platform version, status, and support information."
        >
          <table className="admin-table">
            <tbody>
              <tr>
                <td style={{ fontWeight: 600 }}>Platform Version</td>
                <td>{system.platform_version}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Last Data Sync</td>
                <td>
                  {system.last_data_sync
                    ? new Date(system.last_data_sync).toLocaleString()
                    : "-"}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Database Status</td>
                <td>
                  <Badge tone="success">{system.database_status?.value ?? "-"}</Badge>
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Support Email</td>
                <td>{system.support?.email ?? "-"}</td>
              </tr>
            </tbody>
          </table>
          <div className="adminSettings-info-box">
            <Info
              size={18}
              className="admin-muted"
              style={{ flexShrink: 0, marginTop: "0.125rem" }}
            />
            <div>
              <p style={{ fontWeight: 600, margin: 0 }}>Need Help?</p>
              <p
                className="admin-muted"
                style={{ fontSize: "0.8125rem", margin: 0 }}
              >
                {system.support?.help_message ??
                  "Contact support for assistance during business hours."}
              </p>
            </div>
          </div>
        </Card>
      )}

      {addCohortOpen && (
        <div className="adminUsers-overlay" role="dialog" aria-modal="true">
          <div className="adminUsers-dialog">
            <header className="adminUsers-dialog-header">
              <div>
                <h2 className="adminUsers-dialog-title">Create New Cohort</h2>
                <p className="adminUsers-dialog-desc">
                  Add a new program cohort with start and end dates.
                </p>
              </div>
              <button
                type="button"
                className="admin-btn admin-btn-ghost admin-btn-sm"
                onClick={() => setAddCohortOpen(false)}
              >
                <X size={16} />
              </button>
            </header>
            <div className="adminUsers-dialog-body">
              <div className="adminUsers-field">
                <label className="admin-label">Cohort Name</label>
                <input
                  className="admin-input"
                  style={{ width: "100%" }}
                  value={newCohort.name}
                  onChange={(event) =>
                    setNewCohort((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="e.g., 2024-Q3"
                />
              </div>
              <div className="adminUsers-field">
                <label className="admin-label">Start Date</label>
                <input
                  className="admin-input"
                  style={{ width: "100%" }}
                  type="date"
                  value={newCohort.startDate}
                  onChange={(event) =>
                    setNewCohort((current) => ({
                      ...current,
                      startDate: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="adminUsers-field">
                <label className="admin-label">End Date</label>
                <input
                  className="admin-input"
                  style={{ width: "100%" }}
                  type="date"
                  value={newCohort.endDate}
                  onChange={(event) =>
                    setNewCohort((current) => ({
                      ...current,
                      endDate: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <footer className="adminUsers-dialog-footer">
              <button
                type="button"
                className="admin-btn admin-btn-outline"
                onClick={() => setAddCohortOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={handleAddCohort}
              >
                Create Cohort
              </button>
            </footer>
          </div>
        </div>
      )}

      <ToastViewport />
    </div>
  );
}
